/** Open cloud Share settings from the desktop Share menu. */

import { matchMaybe, toMaybe } from "./maybe";
import { invoke } from "@tauri-apps/api/core";
import {
  authorizedFetch,
  getCloudSession,
  hasCloudBackupEntitlement,
} from "./cloudAuth";
import { ensureCloudProject } from "./cloudSnapshot";
import { isTauri } from "./runtime";
import { trimmedOrNull } from "./trimmedString";

async function readProjectCloudIds(rootPath: string): Promise<{
  clientProjectId: string | null;
  name: string;
}> {
  try {
    const config = await invoke<{
      cloudProjectId?: string | null;
      metadata?: { title?: string };
    }>("read_zw_config", { rootPath });
    const clientProjectId = trimmedOrNull(config.cloudProjectId);
    const title = trimmedOrNull(config.metadata?.title);
    const folderM = toMaybe(
      rootPath
        .split(/[/\\]/)
        .filter((part) => part.length > 0)
        .pop(),
    );
    const name =
      title ??
      matchMaybe(folderM, {
        nothing: () => "Project",
        just: (folder) => (folder.length > 0 ? folder : "Project"),
      });
    return { clientProjectId, name };
  } catch {
    return { clientProjectId: null, name: "Project" };
  }
}

/**
 * Requests a signed handoff URL and opens the cloud Share settings window.
 * Returns an error message for the caller to show, or null on success.
 */
export async function openShareViaCloud(
  rootPath: string | null,
): Promise<string | null> {
  if (isTauri() === false) {
    return "Share via Cloud is available in the desktop app.";
  }
  if (rootPath === null || rootPath.length === 0) {
    return "Open a project before sharing.";
  }
  if (getCloudSession() === null) {
    return "Sign in to Cloud under Settings → Account, then try again.";
  }
  if ((await hasCloudBackupEntitlement()) === false) {
    return "Cloud is required to host shares. Subscribe or redeem Cloud access on your account.";
  }

  const { clientProjectId, name } = await readProjectCloudIds(rootPath);
  if (clientProjectId === null || clientProjectId.length === 0) {
    return "This project is not linked to Cloud yet. Save a cloud backup first, then try again.";
  }

  let cloudId: string;
  try {
    cloudId = await ensureCloudProject(clientProjectId, name);
  } catch (err) {
    return err instanceof Error
      ? err.message
      : "Could not find the cloud project.";
  }

  const res = await authorizedFetch(`/v1/projects/${cloudId}/sharing/handoff`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
  if (res.status === 402) {
    return "Cloud is required to host shares.";
  }
  if (res.ok === false) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    const error = trimmedOrNull(body.error);
    return error ?? `Could not open Share settings (${res.status}).`;
  }
  const body = (await res.json()) as { url?: string };
  const url = trimmedOrNull(body.url);
  if (url === null) {
    return "Cloud did not return a Share URL.";
  }

  try {
    await invoke("open_share_cloud_window", { url });
    return null;
  } catch (err) {
    return shareWindowErrorMessage(err);
  }
}

/** The Rust command rejects with `{ kind, message? }` (#154). */
export function shareWindowErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  const kind =
    typeof err === "object" && err !== null && "kind" in err
      ? (err as { kind: unknown }).kind
      : null;
  switch (kind) {
    case "not_https":
    case "has_user_info":
    case "not_cloud_origin":
    case "invalid":
      return "Refused to open that link because it is not a Zephyrwrite Cloud address.";
    case "unsupported":
      return "Share via Cloud is available on desktop.";
    default:
      return "Could not open the Share window.";
  }
}
